import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Shield, ShieldAlert, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/utils/constants";

/**
 * /otp/:token — mobil hızlı giriş onay sayfası (panel login sayfasından bağımsız)
 */
const OtpLinkApprovePage = ({ isDark = true }) => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadInfo = useCallback(async () => {
    if (!token) {
      setError("Geçersiz onay bağlantısı.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/auth/otp-link`, {
        params: { format: "json", action: "info", t: token },
      });
      const data = res.data || {};
      if (!data.ok) {
        setError(data.message || "Bağlantı geçersiz veya süresi dolmuş.");
        setInfo(null);
        return;
      }
      setInfo(data);
      if (data.already_approved) {
        setSuccess(data.message || "Giriş zaten onaylandı.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Bağlantı doğrulanamadı.");
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  const handleApprove = async () => {
    if (!token || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/auth/otp-link?format=json&action=approve&t=${encodeURIComponent(token)}`,
        { token }
      );
      const data = res.data || {};
      if (!data.ok) {
        setError(data.message || "Onaylanamadı.");
        return;
      }
      setSuccess(data.message || "Giriş onaylandı. Bilgisayarınızdaki oturum açılacak.");
    } catch (err) {
      setError(err.response?.data?.message || "Onay sırasında hata oluştu.");
    } finally {
      setSubmitting(false);
    }
  };

  const bgClass = isDark ? "bg-slate-950 text-slate-100" : "bg-slate-100 text-slate-900";
  const cardClass = isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200";
  const mutedClass = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${bgClass}`}>
      <div className={`w-full max-w-md rounded-2xl border p-6 shadow-xl ${cardClass}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-xl ${isDark ? "bg-cyan-500/15 text-cyan-400" : "bg-cyan-50 text-cyan-700"}`}>
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Giriş Onayı</h1>
            <p className={`text-sm ${mutedClass}`}>E-CDR Panel hızlı giriş</p>
          </div>
        </div>

        {loading && (
          <div className="flex flex-col items-center py-10 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            <p className={`text-sm ${mutedClass}`}>Doğrulanıyor...</p>
          </div>
        )}

        {!loading && error && (
          <div className={`rounded-xl border p-4 flex gap-3 ${isDark ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-800"}`}>
            <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!loading && success && (
          <div className={`rounded-xl border p-4 flex gap-3 ${isDark ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm">{success}</p>
              <p className={`text-xs mt-2 ${mutedClass}`}>Bu pencereyi kapatabilirsiniz.</p>
            </div>
          </div>
        )}

        {!loading && !error && !success && info && (
          <>
            <p className={`text-sm mb-3 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              <strong>{info.username_masked || "***"}</strong> hesabı için panel girişi onayı isteniyor.
            </p>
            <div className={`rounded-xl border p-3 mb-3 text-sm ${isDark ? "border-amber-500/30 bg-amber-500/10 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
              Bu giriş denemesini siz başlatmadıysanız <strong>Reddet</strong> deyin.
            </div>
            <p className={`text-xs mb-1 ${mutedClass}`}>Giriş yapılmak istenen IP adresi:</p>
            <div className={`font-mono text-sm rounded-lg px-3 py-2 mb-4 ${isDark ? "bg-slate-800 border border-slate-700" : "bg-slate-50 border border-slate-200"}`}>
              {info.client_ip || "Bilinmiyor"}
            </div>
            <Button
              type="button"
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white"
              disabled={submitting}
              onClick={handleApprove}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Girişi Onayla
            </Button>
            <Button type="button" variant="ghost" className={`w-full mt-2 ${mutedClass}`} onClick={() => window.close()}>
              Reddet / Kapat
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default OtpLinkApprovePage;
